import { checkAdminAuth, validateAdminToken } from './auth';
import { createAdminToolbar, setupAdminToolbarHandlers, setAdminName, showAdminToolbar, addAdminStyles } from './ui';
import { createSeoModal, setupSeoModalHandlers, openSeoModal, closeSeoModal, loadSeoData, saveSeoData, loadAndApplySeoMetadata } from './seo';
import { getApiBaseUrl } from '../../config/app-config';

let adminState = {
    initialized: false,
    authenticated: false,
    currentPage: '',
    apiUrl: ''
};

/**
 * Инициализирует админ-интерфейс
 */
export async function initAdminInterface() {
    // Проверяем, есть ли на странице атрибут data-page
    if (!document.body.hasAttribute('data-page')) {
        console.log('No data-page attribute found on the page, admin interface not initialized');
        return;
    }

    // Если админ-интерфейс уже инициализирован, выходим
    if (adminState.initialized) {
        console.log('Admin interface already initialized');
        return;
    }

    console.log('Initializing admin interface...');

    // Получаем URL API
    adminState.apiUrl = getApiBaseUrl();

    // Определяем текущую страницу
    adminState.currentPage = document.body.getAttribute('data-page') || 'Home';

    // Добавляем админ-компоненты в DOM
    injectAdminComponents();

    // Добавляем CSS стили
    addAdminStyles();

    // Проверяем авторизацию пользователя
    const authData = await checkAdminAuth();

    if (authData) {
        adminState.authenticated = true;
        console.log('Admin authenticated:', authData.username);

        // Инициализируем админ-панель
        initAdminFunctionality(authData);
    } else {
        adminState.authenticated = false;
        console.log('User not authenticated as admin');
    }

    // Загружаем SEO-метаданные независимо от авторизации
    await loadAndApplySeoMetadata(adminState.apiUrl);

    adminState.initialized = true;
    console.log('Admin interface initialized');
}

/**
 * Создает и добавляет в DOM компоненты админ-интерфейса
 */
function injectAdminComponents() {
    // Создаем элементы админ-панели
    const adminToolbar = createAdminToolbar();
    const seoModal = createSeoModal();

    // Добавляем элементы в DOM
    document.body.insertBefore(adminToolbar, document.body.firstChild);
    document.body.appendChild(seoModal);
}

/**
 * Инициализирует функциональность админ-панели
 * @param {Object} userData Данные авторизованного пользователя
 */
function initAdminFunctionality(userData) {
    // Устанавливаем имя админа
    setAdminName(userData.username);

    // Показываем админ-панель
    showAdminToolbar();

    // Настраиваем обработчики событий для админ-панели
    const adminToolbar = document.getElementById('admin-toolbar');
    if (adminToolbar) {
        setupAdminToolbarHandlers(adminToolbar, () => {
            handleSeoButtonClick();
        });
    }

    // Настраиваем обработчики для SEO-модального окна
    const seoModal = document.getElementById('seo-modal');
    if (seoModal) {
        setupSeoModalHandlers(seoModal, {
            onClose: () => {
                console.log('SEO modal closed');
            },
            onSave: () => {
                handleSeoSave();
            }
        });
    }
}

/**
 * Обработчик нажатия на кнопку SEO
 */
function handleSeoButtonClick() {
    // Re-read data-page at click time so dynamic pages (news/vacancy) resolve after their data loads
    const currentPage = document.body.getAttribute('data-page') || adminState.currentPage;
    loadSeoData(adminState.apiUrl, currentPage);
    openSeoModal(currentPage);
}

/**
 * Обработчик сохранения SEO-настроек
 */
async function handleSeoSave() {
    try {
        const currentPage = document.body.getAttribute('data-page') || adminState.currentPage;
        await saveSeoData(adminState.apiUrl, currentPage);

        // Закрываем модальное окно
        closeSeoModal();
    } catch (error) {
        console.error('Error saving SEO data:', error);
    }
}

/**
 * Проверяет, инициализирован ли админ-интерфейс
 * @returns {boolean} Флаг инициализации
 */
export function isAdminInterfaceInitialized() {
    return adminState.initialized;
}

/**
 * Проверяет, авторизован ли пользователь как админ
 * @returns {boolean} Флаг авторизации
 */
export function isAdminAuthenticated() {
    return adminState.authenticated;
}

/**
 * Сбрасывает состояние админ-интерфейса
 */
export function resetAdminInterface() {
    adminState = {
        initialized: false,
        authenticated: false,
        currentPage: '',
        apiUrl: ''
    };
}